import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import ReservationService from '~/services/ReservationService';

import { allSettledWithConcurrency } from '~/utils/allSettledWithConcurrency';
import getErrorMessage from '~/utils/getErrorMessage';

import { reservationQueryKeys } from './reservationQueryKeys';

export interface ExtraGuestSurchargeWrite {
  operation: 'create' | 'update';
  payload: App.ExtraGuestSurcharges;
  /** Carried through so a failure can name the room a CM recognises rather than an id. */
  roomLabel: string;
  roomRateId: string;
  roomTypeId: string;
}

export interface ExtraGuestSurchargeWriteResult {
  error?: string;
  roomLabel: string;
  roomRateId: string;
  succeeded: boolean;
}

// A stopgap: this batching goes away once svc-reservation grows a bulk extra-guest-surcharge endpoint.
const EGS_WRITE_CONCURRENCY = 5;

/**
 * There is no bulk endpoint, unlike the blackout and surcharge imports this mirrors, so one apply is N
 * independent requests that can half-succeed. `allSettled` keeps one rejection from hiding the writes that
 * landed.
 */
export function useApplyExtraGuestSurcharges(propertyId: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (writes: Array<ExtraGuestSurchargeWrite>): Promise<Array<ExtraGuestSurchargeWriteResult>> => {
      const settled = await allSettledWithConcurrency(writes, EGS_WRITE_CONCURRENCY, (write) =>
        write.operation === 'update'
          ? ReservationService.updateExtraGuestSurcharge(
              write.payload,
              write.payload.id,
              propertyId,
              write.roomTypeId,
              write.roomRateId,
            )
          : ReservationService.createExtraGuestSurcharge(write.payload, propertyId, write.roomTypeId, write.roomRateId),
      );

      return settled.map((outcome, index) => ({
        error: outcome.status === 'rejected' ? getErrorMessage(outcome.reason, 'Unknown error') : undefined,
        roomLabel: writes[index].roomLabel,
        roomRateId: writes[index].roomRateId,
        succeeded: outcome.status === 'fulfilled',
      }));
    },
    // onSettled rather than onSuccess: a partial failure still changed the server, so the tab has to
    // refetch either way or it keeps showing the pre-apply state for the room rates that did save.
    onSettled: (results) => {
      // Empty room rate list on purpose: the stored key appends the ids, so this is their common prefix.
      queryClient.invalidateQueries({
        queryKey: reservationQueryKeys.extraGuestSurchargesByRoomRate(propertyId, []),
      });

      if (!results) {
        return;
      }

      const failed = results.filter((result) => !result.succeeded);
      const savedCount = results.length - failed.length;

      if (failed.length === 0) {
        enqueueSnackbar(`Applied extra guest surcharges to ${savedCount} room ${savedCount === 1 ? 'rate' : 'rates'}`, {
          variant: 'success',
        });
        return;
      }

      const failureDetail = `${failed[0].roomLabel}: ${failed[0].error}`;
      enqueueSnackbar(
        savedCount > 0
          ? `Applied ${savedCount} of ${results.length} room rates. ${failed.length} failed. First error: ${failureDetail}`
          : `Failed to apply extra guest surcharges. First error: ${failureDetail}`,
        { variant: savedCount > 0 ? 'warning' : 'error' },
      );
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to apply extra guest surcharges: ${error.message}`, { variant: 'error' });
    },
  });
}
